import React from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Text,
  TextProps,
  chakra,
  ChakraComponent,
  Flex,
  FlexProps,
  keyframes,
  Skeleton,
  Switch,
  SwitchProps,
} from '@chakra-ui/react';
import { FEATURE_FLAGS } from '@/constants/featureFlags';
import { useQueryOfferings } from '@/api/offerings/offerings.hooks';
import toggle from '@/assets/volcanobet/images/toggle/toggle.png';
import toggleBg from '@/assets/volcanobet/images/toggle/toggle-bg.png';
import toggleBgAtRetina from '@/assets/volcanobet/images/toggle/toggle-bg@2x.png';

const FlexWrapper: ChakraComponent<'div', FlexProps> = chakra(Flex, {
  label: 'ToggleFlexWrapper',
  baseStyle: {
    alignItems: 'center',
    bg: `url(${toggleBg}) center no-repeat`,
    bgSize: 'cover',
    contain: 'layout',
    width: '272px',
    height: '96px',
    justifyContent: 'center',
    mx: 'auto',
    mb: '7',
    pb: '3',
    pt: ['4', null, null, '5'],
    px: '6',
    '@media (-webkit-min-device-pixel-ratio: 2)': {
      bgImg: `url(${toggleBgAtRetina})`,
    },
  },
});

const TextToggle: ChakraComponent<'text', TextProps & { isActive: boolean }> =
  chakra(Text, {
    label: 'TextToggle',
    shouldForwardProp: (prop) => !['isActive'].includes(prop),
    baseStyle: {
      fontFamily: 'Caveman',
      fontWeight: 'normal',
      fontSize: 'lg',
      textTransform: 'uppercase',
      color: 'alpha.500',
      letterSpacing: 'widest',
      px: '2.5',
      pt: '1',
    },
  });

const Switcher: ChakraComponent<'label', SwitchProps> = chakra(Switch, {
  label: 'Switcher',
  baseStyle: {
    '.chakra-switch__track': {
      bg: 'blackAlpha.500',
      boxShadow: 'inset 0 2px 2px rgba(0, 0, 0, .3)',
    },

    '.chakra-switch__thumb': {
      bg: 'transparent',
      pos: 'relative',
      transitionDuration: '0.7s',

      _before: {
        bgImg: `url(${toggle})`,
        height: '4.25rem',
        width: '4.5rem',
        bgPos: 'center',
        bgRepeat: 'no-repeat',
        bgSize: 'contain',
        content: '""',
        pos: 'absolute',
        bottom: '60px',
        left: '-6',
        right: '0',
        top: '-20px',
        animation: `
            ${keyframes`
              from { transform: rotate(0deg) }
              to { transform: rotate(-360deg) }
            `} 0.5s ease-out`,
      },
    },

    _active: {
      '.chakra-switch__track': {
        boxShadow: 'inset 0 2px 2px rgba(0, 0, 0, .3)',
      },
    },

    _checked: {
      '.chakra-switch__thumb': {
        transitionDuration: '0.7s',
        _before: {
          animation: `
            ${keyframes`
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            `} 0.7s ease-out`,
        },
      },
      '.chakra-switch__track': {
        bg: 'blackAlpha.500',
      },
    },
  },
});

export default function Toggle() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { category } = {
    category: searchParams.get('category'),
  };
  const { data, isInitialLoading } = useQueryOfferings();
  const hasSports = FEATURE_FLAGS.HAS_SPORTS_VIEW;

  if (hasSports && isInitialLoading) {
    return <Skeleton sx={{ h: '44px', mb: '2.5' }} />;
  }

  if (hasSports && !data?.offered_sports?.length) {
    return <></>;
  }

  if (
    !FEATURE_FLAGS.HAS_RACE_VIEW ||
    !hasSports ||
    (hasSports && !data?.offered_sports?.length)
  )
    return <></>;

  return (
    <FlexWrapper>
      <TextToggle
        data-cy="racing-toggle-text"
        isActive={!category || category === 'racing'}
      >
        Racing
      </TextToggle>

      <Switcher
        onChange={() => {
          setSearchParams({
            category: !category || category === 'racing' ? 'sport' : 'racing',
          });
        }}
        isChecked={!!category && category !== 'racing'}
        colorScheme="blackAlpha"
        size="lg"
        data-cy="categorySwitcher"
      />
      <TextToggle data-cy="sports-toggle-text" isActive={category === 'sport'}>
        Sports
      </TextToggle>
    </FlexWrapper>
  );
}
